import { enSlugFromEs, esSlugFromEn } from '@/navigation/articleSlugRemap'
import {
    DEFAULT_LOCALE,
    EN_PREFIX,
    EN_TO_ES_SECTION,
    ES_TO_EN_SECTION,
    type LandingLocale,
} from '@/navigation/localeConstants'

import { SITE_ORIGIN } from './constants'

export {
    DEFAULT_LOCALE,
    EN_PREFIX,
    EN_TO_ES_SECTION,
    ES_TO_EN_SECTION,
    LOCALES,
    isLandingLocale,
    type LandingLocale,
} from '@/navigation/localeConstants'

function normalizePath(path: string): string {
    if (path === '') return '/'
    const withSlash = path.startsWith('/') ? path : `/${path}`
    if (withSlash.length > 1 && withSlash.endsWith('/')) return withSlash.slice(0, -1)
    return withSlash
}

/** Strip a leading `/en` prefix. `/en` and `/en/` become `/`. Does not remap sections. */
export function stripLocalePrefix(pathname: string): string {
    if (pathname === EN_PREFIX || pathname === `${EN_PREFIX}/`) return '/'
    if (pathname.startsWith(`${EN_PREFIX}/`)) {
        const rest = pathname.slice(EN_PREFIX.length)
        return rest.length > 0 ? rest : '/'
    }
    return pathname
}

function remapSectionEs(section: string): 'aprender' | 'actualidad-hostelera' | null {
    if (section === 'aprender' || section === 'actualidad-hostelera') return section
    return null
}

function remapEnPathToEs(enPathWithoutLocale: string): string {
    const normalized = normalizePath(enPathWithoutLocale)
    if (normalized === '/') return '/'

    const parts = normalized.split('/').filter(Boolean)
    const [section, ...rest] = parts
    const esSection = EN_TO_ES_SECTION[section]
    if (!esSection) return normalized

    if (rest.length === 0) return `/${esSection}`

    const remapSection = remapSectionEs(esSection)
    const enSlug = rest.join('/')
    const esSlug = remapSection ? esSlugFromEn(remapSection, enSlug) : enSlug
    return `/${esSection}/${esSlug}`
}

function remapEsPathToEn(esPath: string): string {
    const normalized = normalizePath(esPath)
    if (normalized === '/') return EN_PREFIX

    const parts = normalized.split('/').filter(Boolean)
    const [section, ...rest] = parts
    const enSection = ES_TO_EN_SECTION[section]
    if (!enSection) return `${EN_PREFIX}${normalized}`

    if (rest.length === 0) return `${EN_PREFIX}/${enSection}`

    const remapSection = remapSectionEs(section)
    const esSlug = rest.join('/')
    const enSlug = remapSection ? enSlugFromEs(remapSection, esSlug) : esSlug
    return `${EN_PREFIX}/${enSection}/${enSlug}`
}

/**
 * Parse a public URL into locale + locale-free ES path (used for route identity / LocaleSeoLinks).
 * EN translated segments are mapped back to Spanish path segments.
 */
export function parseLocalePath(pathname: string): { locale: LandingLocale; pathname: string } {
    if (pathname === EN_PREFIX || pathname === `${EN_PREFIX}/` || pathname.startsWith(`${EN_PREFIX}/`)) {
        const withoutEn = stripLocalePrefix(pathname)
        return { locale: 'en', pathname: remapEnPathToEs(withoutEn) }
    }
    return { locale: DEFAULT_LOCALE, pathname: normalizePath(pathname) }
}

/**
 * Build a public URL from a locale-free ES path.
 * Keeps hash/query out of `path` — append those after.
 */
export function localizePath(path: string, locale: LandingLocale): string {
    const normalized = normalizePath(path)
    if (locale === DEFAULT_LOCALE) return normalized === '' ? '/' : normalized
    return remapEsPathToEn(normalized)
}

export function absoluteLocalizedUrl(path: string, locale: LandingLocale): string {
    const localized = localizePath(path, locale)
    if (localized === '/') return `${SITE_ORIGIN}/`
    return `${SITE_ORIGIN}${localized}`
}

export function ogLocaleFor(locale: LandingLocale): string {
    return locale === 'en' ? 'en_US' : 'es_ES'
}

export function alternateOgLocaleFor(locale: LandingLocale): string {
    return locale === 'en' ? 'es_ES' : 'en_US'
}

/** Public path patterns for React Router (EN uses translated segments where defined). */
export function publicRoutePath(esRoutePattern: string, locale: LandingLocale): string {
    if (locale === DEFAULT_LOCALE) return esRoutePattern
    if (esRoutePattern === '/') return EN_PREFIX

    const normalized = normalizePath(esRoutePattern)
    const parts = normalized.split('/').filter(Boolean)
    const [section, ...rest] = parts
    const enSection = section ? ES_TO_EN_SECTION[section] : undefined
    if (!enSection) return `${EN_PREFIX}${normalized}`

    if (rest.length === 0) return `${EN_PREFIX}/${enSection}`
    return `${EN_PREFIX}/${enSection}/${rest.join('/')}`
}
